import { useCallback, useEffect, useMemo, useState } from "react";
import { SelectInput, useEntityList } from "@pericles/framework-frontend";

import {
  fetchRoadmapFinalReview,
  fetchRoadmapProjects,
  fetchRoadmapSponsorDossier,
  isMigrationsRequiredError,
  updateRoadmapFinalReview,
  type RoadmapFinalReview,
  type RoadmapMigrationsRequiredDetails,
  type RoadmapProject,
  type RoadmapSponsorDossier,
} from "../api/roadmapClient";

import { EmptyState, PageHeader, Section, Tabs } from "./roadmap/DesignSystem";
import { useI18n } from "../i18n";
import { AtlasMigrationsRequiredCallout } from "./roadmap/AtlasMigrationsRequiredCallout";
import { RoadmapApplicationDetailsPanel } from "./roadmap/RoadmapApplicationDetailsPanel";
import { RoadmapApplicationsPanel } from "./roadmap/RoadmapApplicationsPanel";
import { SegmentationStrategyPanel } from "./roadmap/SegmentationStrategyPanel";
import { ProjectFinalReviewConversationPanel } from "./roadmap/ProjectFinalReviewConversationPanel";

type CustomerListItem = { id: string | number; name?: string };

const FINAL_REVIEW_STATUS_OPTIONS = [
  { value: "", label: "Non defini" },
  { value: "in_review", label: "En revue" },
  { value: "changes_requested", label: "Ajustements demandes" },
  { value: "validated", label: "Valide" },
];

function renderChecklistStatus(status: "done" | "missing" | "watch"): string {
  if (status === "done") return "done";
  if (status === "watch") return "watch";
  return "missing";
}

function buildReadinessClass(sponsorDossier: RoadmapSponsorDossier | null): string {
  if (sponsorDossier?.sponsorReadiness.status === "promotable") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (sponsorDossier?.sponsorReadiness.status === "watch") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-red-200 bg-red-50 text-red-700";
}

export function AiRoadmapFinalReviewScreen(): JSX.Element {
  const { t } = useI18n();
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [finalReview, setFinalReview] = useState<RoadmapFinalReview | null>(null);
  const [sponsorDossier, setSponsorDossier] = useState<RoadmapSponsorDossier | null>(null);
  const [statusInput, setStatusInput] = useState("");
  const [notesInput, setNotesInput] = useState("");
  const [activeTab, setActiveTab] = useState("applications");
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [migrationError, setMigrationError] = useState<RoadmapMigrationsRequiredDetails | null>(null);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOptionsData]);

  const projectOptions = useMemo(() => {
    return projects.map((project) => ({ value: project.id, label: project.name }));
  }, [projects]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      return;
    }
    fetchRoadmapProjects(selectedCustomerId)
      .then((data) => setProjects(data))
      .catch(() => setProjects([]));
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    localStorage.setItem("studio.aiRoadmap.activeCustomer", selectedCustomerId);
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setFinalReview(null);
      setSponsorDossier(null);
      setStatusInput("");
      setNotesInput("");
      setError(null);
      setSaveStatus("idle");
      return;
    }

    localStorage.setItem("studio.aiRoadmap.activeProject", selectedProjectId);
    window.dispatchEvent(new Event("roadmap:project-changed"));

    const loadFinalReview = async () => {
      setLoading(true);
      setError(null);
      setMigrationError(null);
      try {
        const [reviewData, dossierData] = await Promise.all([
          fetchRoadmapFinalReview(selectedProjectId),
          fetchRoadmapSponsorDossier(selectedProjectId),
        ]);
        setFinalReview(reviewData);
        setSponsorDossier(dossierData);
        setStatusInput(reviewData.status ?? "");
        setNotesInput(reviewData.notes ?? "");
      } catch (err) {
        if (isMigrationsRequiredError(err)) {
          setMigrationError(err.details);
          setFinalReview(null);
          setSponsorDossier(null);
          return;
        }
        const message = err instanceof Error ? err.message : "Impossible de charger la revue finale.";
        setError(message);
        setFinalReview(null);
        setSponsorDossier(null);
      } finally {
        setLoading(false);
      }
    };

    void loadFinalReview();
  }, [selectedProjectId]);

  const handleSave = useCallback(async () => {
    if (!selectedProjectId) return;
    setSaveStatus("saving");
    setError(null);
    setMigrationError(null);
    try {
      const updated = await updateRoadmapFinalReview(selectedProjectId, {
        status: statusInput.trim() ? statusInput.trim() : null,
        notes: notesInput.trim() ? notesInput.trim() : null,
      });
      setFinalReview(updated);
      setStatusInput(updated.status ?? "");
      setNotesInput(updated.notes ?? "");
      setSaveStatus("success");
    } catch (err) {
      if (isMigrationsRequiredError(err)) {
        setMigrationError(err.details);
        setSaveStatus("error");
        return;
      }
      const message = err instanceof Error ? err.message : "Impossible d'enregistrer la revue finale.";
      setError(message);
      setSaveStatus("error");
    }
  }, [notesInput, selectedProjectId, statusInput]);

  const lastUpdatedLabel = finalReview?.updatedAt ? new Date(finalReview.updatedAt).toLocaleString() : null;
  const sponsorReadinessClass = buildReadinessClass(sponsorDossier);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Final Review"
        description="Consolidez la segmentation et la revue finale avant validation."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex min-w-[180px] flex-col gap-1 text-xs text-slate-500">
              Client
              <SelectInput
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
              >
                <option value="">Selectionner...</option>
                {customerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            </label>
            <label className="flex min-w-[200px] flex-col gap-1 text-xs text-slate-500">
              Projet
              <SelectInput
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                disabled={!projectOptions.length}
              >
                <option value="">Selectionner...</option>
                {projectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            </label>
          </div>
        }
      />

      <div
        className="sticky z-30 bg-card pb-3 pt-2"
        style={{ top: "calc(var(--app-header-height) + var(--atlas-tabs-height) + 1.5rem)" }}
      >
        <Tabs
          tabs={[
            { id: "segmentation-strategy", label: "Segmentation Strategy" },
            { id: "applications", label: t("atlas.subTabs.applications") },
            { id: "application-details", label: t("atlas.subTabs.applicationDetails") },
            { id: "final-review", label: t("atlas.subTabs.finalReview") },
          ]}
          activeId={activeTab}
          onChange={setActiveTab}
        />
      </div>

      {!selectedProjectId ? (
        <EmptyState
          title="Selectionnez un projet"
          description="Choisissez un projet pour lancer la revue finale."
        />
      ) : migrationError ? (
        <div className="space-y-4">
          <AtlasMigrationsRequiredCallout details={migrationError} />
          <EmptyState
            title="Migrations necessaires"
            description="Appliquez les migrations Prisma pour activer la revue finale."
          />
        </div>
      ) : activeTab === "segmentation-strategy" ? (
        <SegmentationStrategyPanel projectId={selectedProjectId} />
      ) : activeTab === "applications" ? (
        <RoadmapApplicationsPanel projectId={selectedProjectId} />
      ) : activeTab === "application-details" ? (
        <RoadmapApplicationDetailsPanel projectId={selectedProjectId} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <div className="space-y-4">
            <Section title="Conversation">
              <ProjectFinalReviewConversationPanel projectId={selectedProjectId} />
            </Section>

            <Section title="Atlas sponsor dossier v3">
              <div className="space-y-4 text-sm text-foreground" data-testid="atlas-sponsor-dossier-v3">
                {sponsorDossier ? (
                  <>
                    <div className={`rounded-lg border px-3 py-3 ${sponsorReadinessClass}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-xs uppercase tracking-[0.18em] opacity-70">Sponsor readiness</div>
                          <div className="mt-1 text-sm font-semibold">
                            {sponsorDossier.sponsorReadiness.status}
                          </div>
                        </div>
                        <div className="text-right text-xs">
                          <div>Confidence: {sponsorDossier.recommendation.confidence}</div>
                          <div>Foundry export: {sponsorDossier.foundryExport.status}</div>
                        </div>
                      </div>
                      <p className="mt-3 text-sm font-medium">{sponsorDossier.summary.headline}</p>
                      <p className="mt-2 text-xs opacity-80">{sponsorDossier.summary.sponsorOutcome}</p>
                      <p className="mt-2 text-xs opacity-80">{sponsorDossier.summary.nextMove}</p>
                    </div>

                    <div
                      className="rounded-lg border border-slate-200 bg-white/70 px-3 py-3"
                      data-testid="atlas-sponsor-recommendation"
                    >
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Recommendation</div>
                      <div className="mt-2 text-sm font-semibold text-slate-900">
                        {sponsorDossier.recommendation.recommendedApplicationName ?? "No bounded application yet"}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Stack posture: {sponsorDossier.recommendation.recommendedStackOptionId}
                      </div>
                      <p className="mt-3 text-sm text-slate-700">{sponsorDossier.recommendation.rationale}</p>
                      {sponsorDossier.recommendation.why.length ? (
                        <ul className="mt-3 space-y-2 text-xs text-slate-600">
                          {sponsorDossier.recommendation.why.map((item) => (
                            <li key={item}>- {item}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>

                    <div className="grid gap-3 xl:grid-cols-2">
                      <div
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3"
                        data-testid="atlas-promotion-checklist"
                      >
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Promotion checklist</div>
                        <div className="mt-3 space-y-2">
                          {sponsorDossier.promotionChecklist.map((item) => (
                            <div key={item.id} className="rounded border border-slate-200 bg-white px-3 py-2">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-slate-900">{item.label}</div>
                                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                                  {renderChecklistStatus(item.status)}
                                  {item.blocking ? " / blocking" : ""}
                                </div>
                              </div>
                              <p className="mt-1 text-xs text-slate-600">{item.rationale}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Open questions</div>
                        <div className="mt-3 space-y-2">
                          {sponsorDossier.openQuestions.map((question) => (
                            <div key={question.id} className="rounded border border-slate-200 bg-white px-3 py-2">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-slate-900">{question.question}</div>
                                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                                  {question.section}
                                  {question.blocking ? " / blocking" : ""}
                                </div>
                              </div>
                              <p className="mt-1 text-xs text-slate-600">{question.rationale}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 xl:grid-cols-2">
                      <div className="rounded-lg border border-slate-200 bg-white/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Runtime constraints</div>
                        <ul className="mt-3 space-y-2 text-xs text-slate-600">
                          {sponsorDossier.runtimeConstraints.map((item) => (
                            <li key={item}>- {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-white/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Risks</div>
                        <div className="mt-3 space-y-2">
                          {sponsorDossier.risks.map((risk) => (
                            <div key={risk.id} className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-slate-900">{risk.title}</div>
                                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                                  {risk.severity}
                                </div>
                              </div>
                              <p className="mt-1 text-xs text-slate-600">{risk.mitigation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white/70 px-3 py-3">
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Stack options</div>
                      <div className="mt-3 grid gap-3 xl:grid-cols-2">
                        {sponsorDossier.stackOptions.map((option) => (
                          <div key={option.id} className="rounded border border-slate-200 bg-slate-50 px-3 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-medium text-slate-900">{option.label}</div>
                              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                                {option.posture}
                              </div>
                            </div>
                            <p className="mt-2 text-xs text-slate-600">{option.rationale}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded border border-slate-200 bg-slate-50 px-3 py-4 text-xs text-slate-500">
                    Sponsor dossier unavailable for this project.
                  </div>
                )}
              </div>
            </Section>
          </div>

          <Section title="Ajustements finaux">
            <div className="space-y-3 text-sm text-foreground">
              {loading ? <div className="text-xs text-muted-foreground">Chargement...</div> : null}
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Statut de validation finale
                <SelectInput
                  value={statusInput}
                  onChange={(event) => setStatusInput(event.target.value)}
                  disabled={loading || saveStatus === "saving"}
                >
                  {FINAL_REVIEW_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SelectInput>
              </label>

              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Notes d'ajustement
                <textarea
                  className="input min-h-[140px]"
                  placeholder="Synthesez les ajustements finaux..."
                  value={notesInput}
                  onChange={(event) => setNotesInput(event.target.value)}
                  disabled={loading || saveStatus === "saving"}
                />
              </label>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => void handleSave()}
                  disabled={loading || saveStatus === "saving"}
                >
                  {saveStatus === "saving" ? "Enregistrement..." : "Enregistrer"}
                </button>
                {saveStatus === "success" ? (
                  <span className="text-xs text-emerald-600">Enregistre.</span>
                ) : null}
                {lastUpdatedLabel ? (
                  <span className="text-xs text-muted-foreground">
                    Derniere mise a jour: {lastUpdatedLabel}
                  </span>
                ) : null}
              </div>

              {error ? (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </div>
              ) : null}
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
